import {useCookieConsentContext} from '@use-cookie-consent/react';

/**
 * Custom hook for handling initial cookie consent actions in a modal context.
 * This hook provides functionality to accept all cookies or reject optional cookies while keeping necessary ones.
 * It automatically closes the modal after any consent action is taken.
 *
 * @param closeModal Function to close the modal, optionally with a callback.
 * @returns Object containing consent action handlers.
 *
 * @example
 * ```tsx
 * const MyConsentModal = () => {
 *   const [isOpen, setIsOpen] = useState(true);
 *   const closeModal = (onClose?: () => void) => {
 *     setIsOpen(false);
 *     onClose?.();
 *   };
 *
 *   const { handleAcceptAll, handleReject } = useInitialConsent(closeModal);
 *
 *   return (
 *     <Modal isOpen={isOpen}>
 *       <button onClick={handleAcceptAll}>Accept All</button>
 *       <button onClick={handleReject}>Reject Optional</button>
 *     </Modal>
 *   );
 * };
 * ```
 */
export const useInitialConsent = (closeModal: (onClose?: () => void) => void) => {
    const {acceptCookies} = useCookieConsentContext();

    /**
     * Handles accepting all cookies including necessary and statistics cookies.
     * This function sets both necessary and statistics cookies to true and closes the modal.
     * Users choosing this option consent to all tracking and analytics functionality.
     */
    const handleAcceptAll = () => {
        acceptCookies({
            necessary: true,
            statistics: true
        });
        closeModal();
    };

    /**
     * Handles rejecting optional cookies while keeping necessary ones enabled.
     * This function sets necessary cookies to true but statistics cookies to false, then closes the modal.
     * Users choosing this option maintain essential functionality while opting out of analytics tracking.
     */
    const handleReject = () => {
        acceptCookies({
            necessary: true,
            statistics: false
        });
        closeModal();
    };

    return {
        handleAcceptAll,
        handleReject
    };
};